import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { PeriodSelector, periodLabel, periodParams } from "@/components/PeriodSelector";
import { ArrowLeft, UserPlus, Repeat, Landmark } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && (
          <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="mt-3 font-mono-fig text-2xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function Disbursement() {
  const [params, setParams] = useSearchParams();
  const period = params.get("period") || "all";
  const value = params.get("value") || null;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/disbursement", { params: periodParams(period, value) }).then((r) => setData(r.data));
  }, [period, value]);

  const pLabel = periodLabel(period, value);
  const onChange = (p, v) => setParams(v ? { period: p, value: v } : { period: p });

  return (
    <Layout>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <PageHeader
        title="Loan Disbursement"
        subtitle={`Breakdown of capital disbursed · ${pLabel}`}
        action={<PeriodSelector period={period} value={value} onChange={onChange} testid="disbursement-period-selector" />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Stat
          label="Total Disbursed"
          value={fmtRM(data?.total)}
          sub={`${data?.count || 0} loans`}
          icon={Landmark}
          accent="bg-slate-100 text-slate-700"
        />
        <Stat
          label="New Customers"
          value={fmtRM(data?.newAmount)}
          sub={`${data?.newCount || 0} loans`}
          icon={UserPlus}
          accent="bg-green-100 text-green-700"
        />
        <Stat
          label="Repeat Customers"
          value={fmtRM(data?.repeatAmount)}
          sub={`${data?.repeatCount || 0} loans`}
          icon={Repeat}
          accent="bg-blue-100 text-blue-700"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="disbursement-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3 font-medium">Date</th>
              <th className="px-3 py-3 font-medium">Code</th>
              <th className="px-3 py-3 font-medium">Customer</th>
              <th className="px-3 py-3 font-medium">Type</th>
              <th className="px-3 py-3 font-medium">Formula</th>
              <th className="px-3 py-3 font-medium text-right">Loan Apply</th>
              <th className="px-5 py-3 font-medium text-right">Disbursed (Money Used)</th>
            </tr>
          </thead>
          <tbody>
            {data?.loans.map((l) => (
              <tr key={l.code} data-testid={`disb-row-${l.code}`} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{l.dateApplied}</td>
                <td className="px-3 py-3">
                  <Link to={`/loans/${l.code}`} className="font-mono-fig font-medium text-slate-900 hover:underline">{l.code}</Link>
                </td>
                <td className="px-3 py-3 text-slate-700">{l.customerName}</td>
                <td className="px-3 py-3">
                  {l.customerType === "New" ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-green-50 text-green-700 border-green-200">
                      <UserPlus className="h-3 w-3" /> New
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
                      <Repeat className="h-3 w-3" /> Repeat
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 font-mono-fig text-xs text-slate-500">{l.repaymentFormula || "—"}</td>
                <td className="px-3 py-3 text-right font-mono-fig text-slate-600">{fmtRM(l.loanApply)}</td>
                <td className="px-5 py-3 text-right font-mono-fig font-semibold text-slate-900">{fmtRM(l.moneyUsed)}</td>
              </tr>
            ))}
            {data && data.loans.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No loans disbursed in this period.</td></tr>
            )}
          </tbody>
          {data && data.loans.length > 0 && (
            <tfoot>
              <tr className="bg-slate-900 text-white font-semibold">
                <td className="px-5 py-3" colSpan={6}>Total · {pLabel}</td>
                <td className="px-5 py-3 text-right font-mono-fig">{fmtRM(data.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Layout>
  );
}
